// TODO: Use default validation or add custom
import React, { useContext, useState, useEffect, ChangeEvent } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Box, Container, Grid, Stack } from '@mui/material';
import { AccountTypes } from '../../../components/changeUser/accountTypes.enum';
import { di } from '../../../state/inversify.config';
import { PlayerCreatePayload } from '../../../state/reduxRepositories/players/player.interfaces';
import { TextInput } from '../../../components/textInput/textInput';
import { UserNotificationType } from '../../../components/userNotification/userNotificationType.enum';
import { UserNotificationContext } from '../../../components/userNotification/userNotificationContext';
import { AddPlayerDomain, NewPlayerInputs } from '../addPlayer.domain';
import { dateTime } from '../../../time/datetime';
import { isValidEmail } from '../../../helpers/emailValidator';
import { Button } from '../../../components/button/button';
import { Checkbox } from '../../../components/checkBox/checkBox';

interface AddPlayerProps {
  dispatchCreatePlayer: (
    newPlayer: PlayerCreatePayload,
    navigator: NavigateFunction,
    emailToConneqtWith?: string,
  ) => void;
  initialValues?: NewPlayerInputs;
  closeModal: (value: boolean) => void;
}
export function AddPlayer({
  dispatchCreatePlayer,
  closeModal,
  initialValues,
}: AddPlayerProps) {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const { addUserNotification } = useContext(UserNotificationContext);

  const addPlayerDomain = di.get(AddPlayerDomain);
  // const maxNumberOfPlayersReached =
  //   addPlayerDomain.useMaxNumberOfPlayersReachedSelector();
  const accountType = addPlayerDomain.useAccountTypeSelector();

  const [inputs, setInputs] = useState<NewPlayerInputs>(
    initialValues || {
      firstName: undefined,
      lastName: undefined,
      dateOfBirth: undefined,
      guardianEmail: undefined,
    },
  );

  const [successBanner, setSuccessBanner] = useState(false);
  const [showDuplicatePlayerBanner, setShowDuplicatePlayerBanner] =
    useState(false);
  const [isConsentConfirmed, setIsConsentConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setInputs((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!inputs.firstName) {
      setErrorMessage(t('REQUIRED_FIRST_NAME'));
      return;
    }

    if (!inputs.lastName) {
      setErrorMessage(t('REQUIRED_LAST_NAME'));
      return;
    }

    if (!inputs.dateOfBirth) {
      setErrorMessage(t('REQUIRED_DOB'));
      return;
    }

    if (inputs.guardianEmail && !isValidEmail(inputs.guardianEmail)) {
      setErrorMessage(t('INVALID_EMAIL'));
      return;
    }

    if (!addPlayerDomain.isValidDOB(inputs.dateOfBirth)) {
      setErrorMessage(t('INVALID_DOB'));
      return;
    }

    const newPlayer: PlayerCreatePayload = {
      firstName: inputs.firstName?.trim(),
      lastName: inputs.lastName?.trim(),
      dateOfBirth: dateTime.formatToUTCMidnightIsoDateTime(inputs.dateOfBirth),
    };

    if (!addPlayerDomain.isUnique(newPlayer)) {
      setShowDuplicatePlayerBanner(true);
      setSuccessBanner(false);
      return;
    }

    setShowDuplicatePlayerBanner(false);

    // TODO: listen to errors and don't show conneqt modal if player is not created
    dispatchCreatePlayer(newPlayer, navigator, inputs.guardianEmail);

    setInputs({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      guardianEmail: '',
    });
    closeModal(false);
    setSuccessBanner(true);
  };

  useEffect(() => {
    if (successBanner) {
      addUserNotification(
        UserNotificationType.SUCCESS,
        accountType === AccountTypes.Clinic
          ? t('PATIENT_ADDED_SUCCESSFULLY')
          : t('PLAYER_ADDED_SUCCESSFULLY'),
        UserNotificationType.SUCCESS,
      );
    }
    if (showDuplicatePlayerBanner) {
      setErrorMessage(t('PLAYER_ALREADY_EXISTS'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successBanner, showDuplicatePlayerBanner]);

  return (
    <Container maxWidth="md">
      <Box component="form" onSubmit={handleSubmit}>
        <Grid item>
          <TextInput
            name="firstName"
            fullWidth
            autoFocus
            type="text"
            required
            value={inputs.firstName || ''}
            label={t('FIRST_NAME')}
            onChange={handleInputChange}
          />
          <TextInput
            name="lastName"
            type="text"
            fullWidth
            required
            value={inputs.lastName || ''}
            label={t('LAST_NAME')}
            onChange={handleInputChange}
          />
          <TextInput
            name="dateOfBirth"
            type="date"
            fullWidth
            required
            value={inputs.dateOfBirth || ''}
            label={t('DOB')}
            onChange={handleInputChange}
            inputLabelProps={{ shrink: true }}
          />
          {/* TODO: Use this MUI date picker */}
          {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker value={dob} onChange={(date) => setDOB(date)} />
            </LocalizationProvider> */}

          {accountType === AccountTypes.Org && (
            <TextInput
              name="guardianEmail"
              type="email"
              value={inputs.guardianEmail || ''}
              label={t('GUARDIAN_EMAIL')}
              fullWidth
              onChange={handleInputChange}
            />
          )}
          <Grid sx={{ my: 2 }}>
            <Checkbox
              testId="acceptPlayerConcent"
              label={
                <Trans
                  i18nKey="PLAYER_CREATION_CONSENT"
                  values={{
                    user:
                      accountType === AccountTypes.Clinic
                        ? t('PATIENT')
                        : t('PLAYER'),
                  }}
                />
              }
              name="addPlayerConsent"
              checked={isConsentConfirmed}
              onChange={() => setIsConsentConfirmed(!isConsentConfirmed)}
            />
          </Grid>
          {errorMessage && (
            <Stack sx={{ width: '100%', mt: 2 }}>
              <Alert severity="error" onClose={() => {}}>
                {errorMessage}
              </Alert>
            </Stack>
          )}
          <Button
            color="secondary"
            testId="savePlayer"
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            label={t('SAVE')}
            onClick={handleSubmit}
            disabled={!isConsentConfirmed}
          />
        </Grid>
      </Box>
    </Container>
  );
}
